import { useEffect, useRef, type ReactNode } from 'react';
import { Download, Pin } from 'lucide-react';
import { useCanvasStore } from '../../store/canvas-store';
import type { ChatFrame, ChatMessage } from '../../store/types';
import { runChatTurn, cancelChatTurn } from '../../agent/chatTurn';
import { useAgentRunStore, phaseLabel } from '../../agent/agent-run-store';
import { chatTranscriptMarkdown, chatTranscriptFilename } from './chatTranscript';
import { downloadBlob } from '../../io/download';
import { Markdown } from './Markdown';
import { Blob } from '../../render/Blob';
import { useAuth } from '../../auth/useAuth';
import { attributionFor } from './attribution';
import { useReadOnly } from '../readonly';

interface Props { frame: ChatFrame }

/** Split text on `backtick` spans so inline code renders in mono. Text-only —
 *  no HTML is interpreted, so this is injection-safe. */
function withInlineCode(text: string): ReactNode[] {
  return text.split(/(`[^`]+`)/g).map((part, i) =>
    part.startsWith('`') && part.endsWith('`') && part.length > 1 ? (
      <code
        key={i}
        className="font-mono text-[0.92em] px-1 py-px rounded bg-[var(--color-surface-2)]"
      >
        {part.slice(1, -1)}
      </code>
    ) : (
      part
    ),
  );
}

/**
 * Shared chat transcript surface — used by canvas chat nodes AND the editor
 * panel. Calm, scannable "labeled blocks": a muted sender label above each
 * turn, sans prose at a comfortable size, generous spacing. Tool/system steps
 * render as quiet bullet lines so they don't compete with the conversation.
 */
export function ChatFrameView({ frame }: Props) {
  const dispatch = useCanvasStore((s) => s.dispatch);
  const run = useAgentRunStore((s) => s.runs[frame.id]);
  const running = !!run;
  const readOnly = useReadOnly();

  // Auto-scroll the log to the latest message as it streams in.
  const logRef = useRef<HTMLDivElement>(null);
  const lastMsg = frame.messages[frame.messages.length - 1];
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [frame.messages.length, lastMsg?.text]);

  // The canvas attaches a native non-passive wheel listener that preventDefaults
  // + pans/zooms. Stop wheel events natively at the log so they scroll it
  // instead of bubbling up to the canvas gesture handler.
  useEffect(() => {
    const el = logRef.current;
    if (!el) return;
    const stop = (e: WheelEvent) => e.stopPropagation();
    el.addEventListener('wheel', stop);
    return () => el.removeEventListener('wheel', stop);
  }, []);

  const submit = () => {
    if (running || readOnly) return;
    const text = frame.draft.trim();
    if (!text) return;
    void runChatTurn(frame.id, text);
  };

  return (
    <div className="flex flex-col h-full bg-[var(--color-surface)] text-[var(--color-text)]">
      <div
        ref={logRef}
        className="flex-1 min-h-0 overflow-auto p-3 space-y-4 text-[13px]"
        // Canvas sets user-select:none and grabs mousedown for frame drag.
        // Override here so the transcript text is selectable/copyable.
        onMouseDown={(e) => e.stopPropagation()}
        style={{ userSelect: 'text', WebkitUserSelect: 'text', cursor: 'text' }}
      >
        {frame.messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center gap-3 py-8 text-[var(--color-text-muted)] leading-relaxed">
            <Blob size={24} />
            {frame.anchorId ? (
              <p>Scoped to <span className="font-mono">{frame.anchorId}</span>. Tell Viv how to grow it.</p>
            ) : (
              <p>An empty vivarium. What do you want to grow?</p>
            )}
          </div>
        ) : (
          frame.messages.map((m, i) => <Turn key={i} msg={m} />)
        )}
      </div>

      {running && (
        <div className="flex items-center gap-2.5 px-3 py-2 text-[13px] border-t border-[var(--color-border)] text-[var(--color-text-muted)]">
          <Blob size={24} />
          <span className="flex-1">
            {phaseLabel(run.phase)}
            <span className="cf-dots" data-anim aria-hidden><i>.</i><i>.</i><i>.</i></span>
          </span>
          <button
            type="button"
            className="px-2 py-0.5 rounded border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-menu-hover)]"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => cancelChatTurn(frame.id)}
          >
            Stop
          </button>
        </div>
      )}

      <div className="p-3 border-t border-[var(--color-border)] bg-[var(--color-surface-2)]">
        {readOnly ? (
          <div className="w-full px-3 py-2 rounded-[8px] text-[13px] bg-[var(--color-input-bg)] border border-[var(--color-input-border)] text-[var(--color-text-muted)]">
            View-only — you can read this conversation but not send messages.
          </div>
        ) : (
          <textarea
            className="w-full px-3 py-2 rounded-[8px] outline-none resize-none text-[13px] leading-relaxed bg-[var(--color-input-bg)] border border-[var(--color-input-border)] text-[var(--color-text)]"
            rows={2}
            placeholder={frame.anchorId ? 'Tell Viv how to grow this frame…' : 'Tell Viv what to grow…'}
            value={frame.draft}
            onChange={(e) => dispatch({ t: 'chat.setDraft', id: frame.id, draft: e.target.value })}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
        )}
        <div className="flex justify-between items-center mt-1.5 text-[11px] text-[var(--color-text-muted)]">
          <span className="flex items-center gap-1">
            {frame.pinned ? <><Pin size={11} /> pinned</> : 'ephemeral · ⏎ to pin'}
          </span>
          <span className="flex items-center gap-2">
            <span>{frame.messages.length} {frame.messages.length === 1 ? 'message' : 'messages'}</span>
            {frame.messages.length > 0 && (
              <button
                type="button"
                title="Download transcript as Markdown"
                aria-label="Download transcript"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() =>
                  downloadBlob(chatTranscriptFilename(frame), chatTranscriptMarkdown(frame), 'text/markdown')
                }
                className="p-1 rounded border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-menu-hover)] flex items-center"
              >
                <Download size={12} />
              </button>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

/** One conversation turn. System/tool steps render as a quiet bullet line. */
function Turn({ msg }: { msg: ChatMessage }) {
  if (msg.role === 'system') {
    return (
      <div className="flex gap-1.5 text-[12px] leading-relaxed text-[var(--color-text-muted)]">
        <span aria-hidden>·</span>
        <span className="whitespace-pre-wrap">{withInlineCode(msg.text)}</span>
      </div>
    );
  }
  const isUser = msg.role === 'user';
  const selfSub = useAuth((s) => s.email);
  const attr = attributionFor(msg, selfSub);
  return (
    <div>
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold tracking-wide uppercase text-[var(--color-text-muted)]">
        {!isUser && <Blob size={16} glow={false} />}
        {isUser && attr.color && (
          <span className="h-2 w-2 rounded-full" style={{ background: attr.color }} aria-hidden />
        )}
        {attr.label}
      </div>
      {isUser ? (
        // User text is verbatim input — keep it literal (no Markdown surprises).
        <div className="whitespace-pre-wrap leading-relaxed">{withInlineCode(msg.text)}</div>
      ) : (
        // Agent streams GitHub-flavored Markdown — render it.
        <Markdown text={msg.text} />
      )}
    </div>
  );
}
